import React, { useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiShield, FiUpload, FiCheckSquare, FiLock, FiInfo, FiClock } from 'react-icons/fi';
import request from '../utils/request';
import { ESCALATION_REASONS, ESCALATION_CHECKLIST } from '../constants/escalationReasons';

const initialForm = {
  reason: '',
  subreason: '',
  ticketId: '',
  orderId: '',
  customerName: '',
  customerEmail: '',
  impact: '',
  notes: '',
  attachments: '',
  slaRisk: false,
  justification: '',
};

export default function DispatcherEscalate() {
  const [form, setForm] = useState(initialForm);
  const [checklist, setChecklist] = useState(() => ESCALATION_CHECKLIST.map(item => ({ label: item, checked: false })));
  const [subreasons, setSubreasons] = useState([]);
  const [weekCount, setWeekCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function bootstrap() {
      setLoading(true);
      setError(null);
      try {
        const { data: meRes } = await request.get('/employees/me');
        const employee = meRes.employee || meRes;
        if (!mounted) return;
        setEmployeeId(employee._id);
        if (employee._id) {
          const { data: escRes } = await request.get(`/employees/${employee._id}/escalations?limit=10`);
          const count = escRes.weekCount ?? escRes.escalationWeekCount ?? employee.escalationWeekCount ?? 0;
          setWeekCount(count);
          setHistory(escRes.escalations || escRes.items || []);
        }
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || 'Failed to load escalation info');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    bootstrap();
    return () => { mounted = false; };
  }, []);

  const overThreshold = weekCount > 3;

  useEffect(() => {
    const found = ESCALATION_REASONS.find(r => r.id === form.reason);
    setSubreasons(found?.subreasons || []);
    setForm(prev => ({ ...prev, subreason: '' }));
  }, [form.reason]);

  const allChecklistChecked = useMemo(() => checklist.every(item => item.checked), [checklist]);

  const canSubmit = useMemo(() => {
    const requiredFilled = form.reason && form.subreason && form.ticketId && form.orderId && form.notes;
    const justificationOk = !overThreshold || (overThreshold && form.justification.trim().length > 5);
    return requiredFilled && allChecklistChecked && justificationOk && !submitting;
  }, [form, allChecklistChecked, submitting, overThreshold]);

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleChecklist = (idx) => {
    setChecklist(prev => prev.map((item, i) => i === idx ? { ...item, checked: !item.checked } : item));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await request.patch(`/tickets/${form.ticketId}/escalate`, {
        reason: form.reason,
        subreason: form.subreason,
        escalatedBy: employeeId,
        notes: form.notes,
        orderId: form.orderId,
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        impact: form.impact,
        attachments: form.attachments,
        slaRisk: form.slaRisk,
        justification: overThreshold ? form.justification : undefined,
      });
      setSuccess('Escalation submitted to specialized team.');
      setForm(initialForm);
      setChecklist(ESCALATION_CHECKLIST.map(item => ({ label: item, checked: false })));
      if (employeeId) {
        const { data: escRes } = await request.get(`/employees/${employeeId}/escalations?limit=10`);
        const count = escRes.weekCount ?? escRes.escalationWeekCount ?? weekCount + 1;
        setWeekCount(count);
        setHistory(escRes.escalations || escRes.items || []);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to submit escalation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-white font-bold shadow-lg">
            ES
          </div>
          <div>
            <h1 className="text-2xl font-bold">Escalate</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">For true blockers only — routed to specialized team</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge tone={overThreshold ? 'red' : weekCount > 0 ? 'amber' : 'emerald'}>{weekCount} this week</Badge>
        </div>
      </header>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 flex items-start gap-4">
        <div className="p-2 rounded-xl bg-red-100 text-red-700">
          <FiShield className="w-5 h-5" />
        </div>
        <div className="flex-1 space-y-1">
          <p className="font-semibold text-red-700 dark:text-red-400">Use only when blocked.</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Escalations route to a specialized team. Overuse is monitored; verify playbooks and complete checklist before submitting.
          </p>
          {overThreshold && (
            <p className="text-sm text-red-600 font-medium flex items-center gap-2">
              <FiAlertTriangle className="w-4 h-4" /> You exceeded 3 escalations this week. Add justification before submitting.
            </p>
          )}
        </div>
      </div>

      {error && <div className="p-3 rounded-xl bg-red-50 text-red-700 border border-red-200">{error}</div>}
      {success && <div className="p-3 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200">{success}</div>}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <div className="flex items-center gap-2">
              <FiLock className="w-4 h-4 text-gray-400" />
              <h2 className="text-lg font-semibold">Escalation Form</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                label="Reason"
                value={form.reason}
                onChange={v => updateField('reason', v)}
                options={ESCALATION_REASONS.map(r => ({ value: r.id, label: r.label }))}
                required
              />
              <SelectField
                label="Sub-reason"
                value={form.subreason}
                onChange={v => updateField('subreason', v)}
                options={subreasons.map(sr => ({ value: sr.id, label: sr.label }))}
                required
                disabled={!form.reason}
              />
              <InputField label="Ticket ID" value={form.ticketId} onChange={v => updateField('ticketId', v)} required />
              <InputField label="Order ID" value={form.orderId} onChange={v => updateField('orderId', v)} required />
              <InputField label="Customer Name" value={form.customerName} onChange={v => updateField('customerName', v)} />
              <InputField label="Customer Email" value={form.customerEmail} onChange={v => updateField('customerEmail', v)} />
              <InputField label="Impact (one line)" value={form.impact} onChange={v => updateField('impact', v)} placeholder="e.g., SLA at risk in 30 min" />
              <InputField label="Attachments (link)" value={form.attachments} onChange={v => updateField('attachments', v)} icon={<FiUpload className="w-4 h-4 text-gray-400" />} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes / Evidence</label>
              <textarea
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-sm"
                rows={4}
                value={form.notes}
                onChange={e => updateField('notes', e.target.value)}
                placeholder="What is blocking you? What did you try? Paste evidence or links."
                required
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.slaRisk} onChange={e => updateField('slaRisk', e.target.checked)} />
              SLA at risk
            </label>
            {overThreshold && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-red-700">Justification (required over 3/week)</label>
                <textarea
                  className="w-full rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900"
                  rows={3}
                  value={form.justification}
                  onChange={e => updateField('justification', e.target.value)}
                  placeholder="Explain why this could not be resolved without escalation."
                  required
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center gap-2 mb-3">
              <FiCheckSquare className="w-4 h-4 text-gray-400" />
              <h3 className="text-md font-semibold">Pre-submit checklist</h3>
            </div>
            <div className="space-y-2">
              {checklist.map((item, idx) => (
                <label key={item.label} className="flex items-start gap-3 text-sm">
                  <input type="checkbox" checked={item.checked} onChange={() => toggleChecklist(idx)} />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center gap-2 mb-2">
              <FiInfo className="w-4 h-4 text-gray-400" />
              <h3 className="text-md font-semibold">Recent escalations</h3>
              <Badge tone={weekCount > 0 ? 'amber' : 'gray'}>{weekCount} this week</Badge>
            </div>
            {history.length === 0 ? (
              <p className="text-sm text-gray-500">No escalations logged.</p>
            ) : (
              <div className="space-y-3">
                {history.map(item => (
                  <div key={`${item.ticketId}-${item.escalatedAt}`} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between text-sm">
                      <p className="font-medium">{item.reason || 'Reason not set'}</p>
                      <Badge tone="gray">{item.status || 'open'}</Badge>
                    </div>
                    <p className="text-xs text-gray-500">Subreason: {item.subreason || '—'}</p>
                    <p className="text-xs text-gray-500">Ticket {item.ticketId || '—'} · Order {item.orderId || '—'}</p>
                    <p className="text-[11px] text-gray-400 flex items-center gap-1">
                      <FiClock className="w-3 h-3" /> {item.escalatedAt ? new Date(item.escalatedAt).toLocaleString() : '—'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-rose-600 shadow-lg shadow-red-500/20 disabled:opacity-60"
          >
            {submitting ? 'Submitting...' : 'Submit Escalation'}
          </button>
        </div>
      </form>
    </div>
  );
}

function InputField({ label, value, onChange, required, placeholder, icon }) {
  return (
    <label className="text-sm space-y-1">
      <span className="text-gray-700 dark:text-gray-200 flex items-center gap-2">{icon}{label}{required && <span className="text-red-500">*</span>}</span>
      <input
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, required, disabled }) {
  return (
    <label className="text-sm space-y-1">
      <span className="text-gray-700 dark:text-gray-200 flex items-center gap-1">{label}{required && <span className="text-red-500">*</span>}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
      >
        <option value="">Select</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </label>
  );
}

function Badge({ children, tone = 'gray' }) {
  const map = {
    gray: 'bg-gray-100 text-gray-700',
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map[tone] || map.gray}`}>
      {children}
    </span>
  );
}




